import React, { useEffect, useCallback, useState } from 'react';
import { useTheme } from '../../../basic/ThemeProvider';
import ThemeSwitcher from '../../../basic/ThemeSwitcher';
import Typography from '../../../basic/Typography';
import { useNewsEditor } from '../context/NewsEditorContext';
import { NewsEditorPageProps, NewsJob, NewsEvent, RerunAggregationParams } from '../types';
import JobSelector from './JobSelector';
import PipelineStageEditor from './PipelineStageEditor';
import NewsAggregationEditor from './NewsAggregationEditor';
import ScriptGenerationEditor from './ScriptGenerationEditor';
import RenderingEditor from './RenderingEditor';
import CompositionEditor from './CompositionEditor';
import newsEditorApi from '../services/newsEditorApi';
import styles from '../NewsEditor.module.css';

const NewsEditorPage: React.FC<NewsEditorPageProps> = ({ initialJobId }) => {
  const { isDark, toggleTheme } = useTheme();
  const { state, dispatch } = useNewsEditor();

  // Navigate back to NewsNG
  const handleBackToNewsNG = useCallback(() => {
    window.location.href = '/game/newsng';
  }, []);

  // Load jobs on component mount
  useEffect(() => {
    const loadJobs = async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        dispatch({ type: 'SET_ERROR', payload: null });

        // Load real jobs from API
        const jobs = await newsEditorApi.getJobs();
        console.log('Loaded jobs:', jobs);

        // Transform jobs to match our interface and add stage information
        const transformedJobs = await Promise.all(
          jobs.map(async (job: any) => {
            try {
              // Get job status to determine stage information
              const jobStatus = await newsEditorApi.getJobStatus(job.job_id);

              // Get job metadata including query topic
              const jobMetadata = await newsEditorApi.getJobMetadata(job.job_id);

              return {
                job_id: job.job_id,
                overall_status: job.overall_status as 'pending' | 'processing' | 'completed' | 'failed',
                created_at: job.created_at,
                updated_at: job.updated_at,
                // Add job metadata
                query_topic: jobMetadata?.query?.topic,
                query_location: jobMetadata?.query?.location,
                query_search_level: jobMetadata?.query?.search_level,
                query_language: jobMetadata?.query?.language,
                query_max_events: jobMetadata?.query?.max_events,
                total_events: jobMetadata?.results?.total_events,
                total_sources_searched: jobMetadata?.results?.total_sources_searched,
                processing_time_seconds: jobMetadata?.results?.processing_time_seconds,
                stages: {
                  aggregation: {
                    status: (jobStatus.aggregation?.status || 'pending') as 'pending' | 'processing' | 'completed' | 'failed',
                    last_updated: jobStatus.aggregation?.last_updated || job.created_at || new Date().toISOString(),
                    progress: jobStatus.aggregation?.progress || 0
                  },
                  script_generation: {
                    status: (jobStatus.script_generation?.status || 'pending') as 'pending' | 'processing' | 'completed' | 'failed',
                    last_updated: jobStatus.script_generation?.last_updated || job.created_at || new Date().toISOString(),
                    progress: jobStatus.script_generation?.progress || 0
                  },
                  rendering: {
                    status: (jobStatus.rendering?.status || 'pending') as 'pending' | 'processing' | 'completed' | 'failed',
                    last_updated: jobStatus.rendering?.last_updated || job.created_at || new Date().toISOString(),
                    progress: jobStatus.rendering?.progress || 0
                  },
                  composition: {
                    status: (jobStatus.composition?.status || 'pending') as 'pending' | 'processing' | 'completed' | 'failed',
                    last_updated: jobStatus.composition?.last_updated || job.created_at || new Date().toISOString(),
                    progress: jobStatus.composition?.progress || 0
                  }
                }
              };
            } catch (error) {
              console.error(`Error loading status for job ${job.job_id}:`, error);
              // Return job with default stage status if status loading fails
              return {
                job_id: job.job_id,
                overall_status: job.overall_status as 'pending' | 'processing' | 'completed' | 'failed',
                created_at: job.created_at,
                updated_at: job.updated_at,
                // Metadata will be undefined if loading failed
                stages: {
                  aggregation: { status: 'pending' as const, last_updated: job.created_at || new Date().toISOString(), progress: 0 },
                  script_generation: { status: 'pending' as const, last_updated: job.created_at || new Date().toISOString(), progress: 0 },
                  rendering: { status: 'pending' as const, last_updated: job.created_at || new Date().toISOString(), progress: 0 },
                  composition: { status: 'pending' as const, last_updated: job.created_at || new Date().toISOString(), progress: 0 }
                }
              };
            }
          })
        );

        dispatch({ type: 'SET_JOBS', payload: transformedJobs });

        // Set initial job if provided
        if (initialJobId) {
          const targetJob = transformedJobs.find((job: any) => job.job_id === initialJobId);
                          if (targetJob) {
                  // Load news aggregation and script generation for initial job
                  try {
                    console.log('About to load initial job data for:', targetJob.job_id);
                    const [newsAggregation, scriptGeneration] = await Promise.all([
                      newsEditorApi.getNewsAggregation(targetJob.job_id),
                      newsEditorApi.getScriptGeneration(targetJob.job_id)
                    ]);
                    console.log('Loaded news aggregation for initial job:', newsAggregation);
                    console.log('Loaded script generation for initial job:', scriptGeneration);
                    console.log('Initial script generation scenes count:', scriptGeneration?.scenes?.length || 0);
                    const jobWithStageData = { ...targetJob, newsAggregation, scriptGeneration };
                    dispatch({ type: 'SET_CURRENT_JOB', payload: jobWithStageData });
                  } catch (error) {
                    console.error('Error loading stage data for initial job:', error);
                    dispatch({ type: 'SET_CURRENT_JOB', payload: targetJob });
                  }
                }
                        } else if (transformedJobs.length > 0) {
                  // Set first job as default and load its stage data
                  const firstJob = transformedJobs[0];
                  try {
                    console.log('About to load default job data for:', firstJob.job_id);
                    const [newsAggregation, scriptGeneration] = await Promise.all([
                      newsEditorApi.getNewsAggregation(firstJob.job_id),
                      newsEditorApi.getScriptGeneration(firstJob.job_id)
                    ]);
                    console.log('Loaded news aggregation for default job:', newsAggregation);
                    console.log('Loaded script generation for default job:', scriptGeneration);
                    console.log('Default script generation scenes count:', scriptGeneration?.scenes?.length || 0);
                    const jobWithStageData = { ...firstJob, newsAggregation, scriptGeneration };
                    dispatch({ type: 'SET_CURRENT_JOB', payload: jobWithStageData });
                  } catch (error) {
                    console.error('Error loading stage data for default job:', error);
                    dispatch({ type: 'SET_CURRENT_JOB', payload: firstJob });
                  }
                }
      } catch (error) {
        console.error('Failed to load jobs:', error);
        dispatch({ type: 'SET_ERROR', payload: 'Failed to load jobs' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    loadJobs();
  }, [initialJobId, dispatch]);

      const handleJobSelect = useCallback(async (job: NewsJob) => {
    try {
      // Load news aggregation data for the selected job
      const newsAggregation = await newsEditorApi.getNewsAggregation(job.job_id);
      console.log('Loaded news aggregation for selected job:', newsAggregation);

      // Load script generation data for the selected job
      console.log('About to load script generation for job:', job.job_id);
      console.log('Job details:', job);
      const scriptGeneration = await newsEditorApi.getScriptGeneration(job.job_id);
      console.log('Loaded script generation for selected job:', scriptGeneration);
      console.log('Script generation scenes count:', scriptGeneration?.scenes?.length || 0);
      console.log('Script generation scenes:', scriptGeneration?.scenes);

      // Load job metadata if not already present
      let jobMetadata = null;
      if (!job.query_topic) {
        jobMetadata = await newsEditorApi.getJobMetadata(job.job_id);
      }

      // Update the job with all stage data and metadata
      const jobWithStageData = {
        ...job,
        ...(jobMetadata && {
          query_topic: jobMetadata.query?.topic,
          query_location: jobMetadata.query?.location,
          query_search_level: jobMetadata.query?.search_level,
          query_language: jobMetadata.query?.language,
          query_max_events: jobMetadata.query?.max_events,
          total_events: jobMetadata.results?.total_events,
          total_sources_searched: jobMetadata.results?.total_sources_searched,
          processing_time_seconds: jobMetadata.results?.processing_time_seconds,
        }),
        newsAggregation,
        scriptGeneration
      };

      dispatch({ type: 'SET_CURRENT_JOB', payload: jobWithStageData });
    } catch (error) {
      console.error('Error loading job data:', error);
      // Set job without stage data if loading fails
      dispatch({ type: 'SET_CURRENT_JOB', payload: job });
    }
  }, [dispatch]);

  // Polling state for script generation
  const [scriptGenerationPolling, setScriptGenerationPolling] = useState<{
    jobId: string;
    intervalId: NodeJS.Timeout | null;
    isPolling: boolean;
  }>({ jobId: '', intervalId: null, isPolling: false });

  // Polling state for aggregation
  const [aggregationPolling, setAggregationPolling] = useState<{
    jobId: string;
    intervalId: NodeJS.Timeout | null;
    isPolling: boolean;
  }>({ jobId: '', intervalId: null, isPolling: false });

  // New job creation state
  const [showNewJobForm, setShowNewJobForm] = useState(false);

  // Start polling for script generation status
  const startScriptGenerationPolling = useCallback((jobId: string) => {
    // Clear any existing polling
    if (scriptGenerationPolling.intervalId) {
      clearInterval(scriptGenerationPolling.intervalId);
    }

    console.log(`Starting status polling for script generation job: ${jobId}`);

    const intervalId = setInterval(async () => {
      try {
        const status = await newsEditorApi.getScriptGenerationStatus(jobId);
        console.log(`Script generation status for ${jobId}:`, status);

                // Update job status with progress
        const newStatus = status.status === 'completed' ? 'completed' : 'processing';

        console.log(`Updating job status for ${jobId}:`, {
          status: newStatus,
          progress: status.progress,
          message: status.message
        });

        dispatch({
          type: 'UPDATE_JOB_STATUS',
          payload: {
            jobId: jobId,
            stage: 'script_generation',
            status: {
              status: newStatus,
              progress: status.progress,
              last_updated: new Date().toISOString(),
              error: undefined // Backend doesn't provide error status for scriptgen
            }
          }
        });

        // Stop polling only when completed (backend doesn't provide error status)
        if (status.status === 'completed') {
          clearInterval(intervalId);
          setScriptGenerationPolling({ jobId: '', intervalId: null, isPolling: false });
          console.log(`✅ Script generation completed successfully for job ${jobId}`);
        }
      } catch (error) {
        console.error(`Error polling script generation status for ${jobId}:`, error);
      }
    }, 5000); // Poll every 5 seconds

    setScriptGenerationPolling({ jobId, intervalId, isPolling: true });
  }, [dispatch, scriptGenerationPolling.intervalId]);

  // Start polling for aggregation status
  const startAggregationPolling = useCallback((jobId: string) => {
    // Clear any existing polling
    if (aggregationPolling.intervalId) {
      clearInterval(aggregationPolling.intervalId);
    }

    console.log(`Starting status polling for aggregation job: ${jobId}`);

    const intervalId = setInterval(async () => {
      try {
        // Check if aggregation is complete by looking for the output file
        const response = await fetch(`/news/out/${jobId}/newsevent_stage/newsevent_stage_aggregation.json`);

        if (response.ok) {
          // Aggregation is complete, stop polling
          clearInterval(intervalId);
          setAggregationPolling({ jobId: '', intervalId: null, isPolling: false });

          // Update job status to completed
          dispatch({
            type: 'UPDATE_JOB_STATUS',
            payload: {
              jobId: jobId,
              stage: 'aggregation',
              status: {
                status: 'completed',
                last_updated: new Date().toISOString(),
                progress: 100
              }
            }
          });

          console.log(`Aggregation completed for job: ${jobId}`);

          // Refresh the current job data
          if (state.currentJob && state.currentJob.job_id === jobId) {
            try {
              const newsAggregation = await newsEditorApi.getNewsAggregation(jobId);
              const updatedJob = { ...state.currentJob, newsAggregation };
              dispatch({ type: 'SET_CURRENT_JOB', payload: updatedJob });
            } catch (error) {
              console.error('Error refreshing job data after aggregation:', error);
            }
          }
        }
      } catch (error) {
        // File doesn't exist yet, continue polling
        console.log(`Aggregation not ready yet for ${jobId}:`, error);
      }
    }, 5000); // Poll every 5 seconds

    setAggregationPolling({ jobId, intervalId, isPolling: true });
  }, [dispatch, aggregationPolling.intervalId, state.currentJob]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (scriptGenerationPolling.intervalId) {
        clearInterval(scriptGenerationPolling.intervalId);
      }
      if (aggregationPolling.intervalId) {
        clearInterval(aggregationPolling.intervalId);
      }
    };
  }, [scriptGenerationPolling.intervalId, aggregationPolling.intervalId]);

  const handleStageRerun = useCallback(async (stageKey: string, aggregationParams?: any) => {
    if (!state.currentJob) return;

    console.log(`Rerunning stage: ${stageKey} for job: ${state.currentJob.job_id}`);

    try {
      // Handle script generation stage specifically
      if (stageKey === 'script_generation') {
        const result = await newsEditorApi.triggerScriptGeneration(state.currentJob.job_id);
        console.log('Script generation triggered successfully:', result);

        // Start polling for status updates
        startScriptGenerationPolling(state.currentJob.job_id);
      } else if (stageKey === 'aggregation' && aggregationParams) {
        // Handle aggregation rerun with custom parameters
        console.log('Rerunning aggregation with params:', aggregationParams);

        // Call the backend aggregation endpoint to overwrite the existing job
        const response = await fetch('/news/api/newsevents/aggregate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pipeline_job: { job_uuid: state.currentJob.job_id }, // Use existing job ID to overwrite
            topic: aggregationParams.topic,
            location: aggregationParams.location,
            search_level: aggregationParams.search_level,
            language: aggregationParams.language,
            max_events: aggregationParams.max_events,
            enable_enrichment: aggregationParams.enable_enrichment,
            aggregation_source: aggregationParams.aggregation_source,
            timerange: aggregationParams.timerange,
            search_after_date: aggregationParams.search_after_date,
            search_before_date: aggregationParams.search_before_date
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log('Aggregation rerun started successfully:', result);

        // Start polling for aggregation completion
        startAggregationPolling(state.currentJob.job_id);
      } else {
        // Call the real API to rerun other stages
        await newsEditorApi.rerunStage(state.currentJob.job_id, stageKey);
      }

      // Update the status to show it's processing
      dispatch({
        type: 'UPDATE_JOB_STATUS',
        payload: {
          jobId: state.currentJob.job_id,
          stage: stageKey,
          status: {
            status: 'processing',
            last_updated: new Date().toISOString(),
            progress: 0
          }
        }
      });
    } catch (error) {
      console.error(`Error rerunning stage ${stageKey}:`, error);
      // Update status to show error
      dispatch({
        type: 'UPDATE_JOB_STATUS',
        payload: {
          jobId: state.currentJob.job_id,
          stage: stageKey,
          status: {
            status: 'failed',
            last_updated: new Date().toISOString(),
            error: error instanceof Error ? error.message : 'Failed to rerun stage'
          }
        }
      });
    }
  }, [state.currentJob, dispatch, startScriptGenerationPolling, startAggregationPolling]);



  // Handler for submitting new job creation
  const handleSubmitNewJob = useCallback(async (params: RerunAggregationParams) => {
    try {
      // Generate a new job ID
      const newJobId = crypto.randomUUID();
      console.log('Creating new job with ID:', newJobId);

      // Call the news aggregation endpoint to create the new job
      const response = await fetch('/news/api/newsevents/aggregate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pipeline_job: { job_uuid: newJobId },
          topic: params.topic,
          location: params.location,
          search_level: params.search_level,
          language: params.language,
          max_events: params.max_events,
          enable_enrichment: params.enable_enrichment,
          aggregation_source: params.aggregation_source,
          timerange: params.timerange,
          search_after_date: params.search_after_date,
          search_before_date: params.search_before_date
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('New job created successfully:', result);

      // Close the form
      setShowNewJobForm(false);

      // Refresh the jobs list
      const updatedJobs = await newsEditorApi.getJobs();
      dispatch({ type: 'SET_JOBS', payload: updatedJobs });

      // Select the new job
      const newJob = updatedJobs.find((job: any) => job.job_id === newJobId);
      if (newJob) {
        handleJobSelect(newJob);
      }

    } catch (error) {
      console.error('Error creating new job:', error);
      alert('Failed to create new job');
    }
  }, [dispatch, handleJobSelect]);

  // Special handler for rendering stage rerun with cast and studio selection
  const handleRenderingRerun = useCallback(async (castAgents: any[], studioLocation: any) => {
    if (!state.currentJob) return;

    console.log(`Rerunning rendering stage for job: ${state.currentJob.job_id}`);
    console.log('Selected cast:', castAgents);
    console.log('Selected studio:', studioLocation);

    try {
      // For now, we'll simulate the rerun process
      // In the future, this would call the actual rendering API with cast and studio parameters
      console.log('Starting rendering rerun with:', {
        jobId: state.currentJob.job_id,
        cast: castAgents.map(c => c.name),
        studio: studioLocation.name
      });

      // Simulate processing time
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Update the status to show it's processing
      dispatch({
        type: 'UPDATE_JOB_STATUS',
        payload: {
          jobId: state.currentJob.job_id,
          stage: 'rendering',
          status: {
            status: 'processing',
            last_updated: new Date().toISOString(),
            progress: 50
          }
        }
      });

      console.log('Rendering rerun started successfully');
    } catch (error) {
      console.error('Error during rendering rerun:', error);
      // Update status to show error
      dispatch({
        type: 'UPDATE_JOB_STATUS',
        payload: {
          jobId: state.currentJob.job_id,
          stage: 'rendering',
          status: {
            status: 'failed',
            last_updated: new Date().toISOString(),
            error: error instanceof Error ? error.message : 'Failed to rerun rendering'
          }
        }
      });
    }
  }, [state.currentJob, dispatch]);

  if (state.isLoading) {
    return (
              <div className={styles['news-editor-page']}>
          <div className={styles['news-editor-loading']}>
            <div className={styles['loading-spinner']}></div>
          <p>Loading NewsEditor...</p>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
              <div className={styles['news-editor-page']}>
          <div className={styles['news-editor-error']}>
          <h3>Error</h3>
          <p>{state.error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles['news-editor-page']}>
      {/* Header */}
      <header className={styles['news-editor-header']}>
        <div className={styles['news-editor-header-left']}>
          <button
            className={styles['news-editor-vmonad-logo-button']}
            onClick={() => window.location.href = '/game/'}
            title="Return to main page"
          >
            <img
              src="/news/demo/public/assets/logo.png"
              alt="vMonad Logo"
              className={styles['news-editor-vmonad-logo']}
            />
          </button>
          <Typography variant="h1" className={styles['news-editor-title']}>News Editor</Typography>
        </div>
        <div className={styles['news-editor-header-right']}>
          <ThemeSwitcher isDark={isDark} onToggle={toggleTheme} />
          <button
            className={styles['news-editor-close-button']}
            onClick={handleBackToNewsNG}
            title="Close and return to NewsNG"
          >
            ✕
          </button>
        </div>
      </header>

                  {/* Main Content */}
      <main className={styles['news-editor-content']}>
        {/* Create New Job Pipeline Stage */}
        <section className={styles['pipeline-stage-section']}>
          <PipelineStageEditor
            stageName="Create New Job"
            stageKey="create_new"
            status={{
              status: showNewJobForm ? 'processing' : 'pending',
              last_updated: new Date().toISOString(),
              progress: showNewJobForm ? 50 : 0
            }}
            isExpanded={showNewJobForm}
            onToggleExpansion={() => setShowNewJobForm(!showNewJobForm)}
            onRerun={() => {}} // No rerun for create stage
          >
            {showNewJobForm && (
              <div className={styles['create-new-job-content']}>
                <NewsAggregationEditor
                  jobId="new"
                  aggregation={{
                    news_events: [],
                    metadata: {
                      total_events: 0,
                      last_updated: new Date().toISOString(),
                      search_query: '',
                      search_level: 'low' as const
                    }
                  }}
                  onUpdate={() => {}}
                  onRerun={handleSubmitNewJob}
                  queryTopic=""
                  isNewJob={true}
                />
              </div>
            )}
          </PipelineStageEditor>
        </section>

        {/* Job Selector */}
        <section className={styles['job-selector-section']}>
          <JobSelector
            jobs={state.jobs}
            currentJob={state.currentJob}
            onJobSelect={handleJobSelect}
            isLoading={state.isLoading}
          />
        </section>

        {/* Job Metadata Display */}
        {state.currentJob && (
          <section className={styles['job-metadata-section']}>
            <Typography variant="h3" gutterBottom={true}>Job Details</Typography>
            <div className={styles['job-metadata-grid']}>
              <div className={styles['job-metadata-item']}>
                <label>Query Topic:</label>
                <span className={styles['job-metadata-value']}>
                  {state.currentJob.query_topic || 'Not specified'}
                </span>
              </div>
              <div className={styles['job-metadata-item']}>
                <label>Location:</label>
                <span className={styles['job-metadata-value']}>
                  {state.currentJob.query_location || 'Not specified'}
                </span>
              </div>
              <div className="job-metadata-item">
                <label>Search Level:</label>
                <span className={styles['job-metadata-value']}>
                  {state.currentJob.query_search_level || 'Not specified'}
                </span>
              </div>
              <div className="job-metadata-item">
                <label>Language:</label>
                <span className={styles['job-metadata-value']}>
                  {state.currentJob.query_language || 'Not specified'}
                </span>
              </div>
              <div className={styles['job-metadata-item']}>
                <label>Max Events:</label>
                <span className={styles['job-metadata-value']}>
                  {state.currentJob.query_max_events || 'Not specified'}
                </span>
              </div>
              <div className={styles['job-metadata-item']}>
                <label>Total Events Found:</label>
                <span className={styles['job-metadata-value']}>
                  {state.currentJob.total_events || 'Not available'}
                </span>
              </div>
              <div className={styles['job-metadata-item']}>
                <label>Sources Searched:</label>
                <span className={styles['job-metadata-value']}>
                  {state.currentJob.total_sources_searched || 'Not available'}
                </span>
              </div>
              <div className={styles['job-metadata-item']}>
                <label>Processing Time:</label>
                <span className={styles['job-metadata-value']}>
                  {state.currentJob.processing_time_seconds
                    ? `${state.currentJob.processing_time_seconds.toFixed(1)}s`
                    : 'Not available'
                  }
                </span>
              </div>
              <div className={styles['job-metadata-item']}>
                <label>Created:</label>
                <span className={styles['job-metadata-value']}>
                  {state.currentJob.created_at
                    ? new Date(state.currentJob.created_at).toLocaleString()
                    : 'Unknown'
                  }
                </span>
              </div>
              <div className={styles['job-metadata-item']}>
                <label>Updated:</label>
                <span className={styles['job-metadata-value']}>
                  {state.currentJob.updated_at
                    ? new Date(state.currentJob.updated_at).toLocaleString()
                    : 'Unknown'
                  }
                </span>
              </div>
            </div>
          </section>
        )}

        {/* Pipeline Stages */}
        {state.currentJob && (
          <section className={styles['pipeline-stages-section']}>
            <Typography variant="h2" gutterBottom={true}>Pipeline Stages</Typography>

            {/* News Aggregation Stage */}
            <PipelineStageEditor
              stageName="News Aggregation"
              stageKey="aggregation"
              status={state.currentJob.stages.aggregation}
              isExpanded={state.expandedStages.includes('aggregation')}
              onToggleExpansion={() => dispatch({ type: 'TOGGLE_STAGE_EXPANSION', payload: 'aggregation' })}
              onRerun={() => handleStageRerun('aggregation')}
            >
              <NewsAggregationEditor
                jobId={state.currentJob.job_id}
                aggregation={state.currentJob.newsAggregation || {
                  news_events: [],
                  metadata: {
                    total_events: 0,
                    last_updated: new Date().toISOString(),
                    search_query: '',
                    search_level: 'medium'
                  }
                }}
                onUpdate={async (updatedEvents: NewsEvent[]) => {
                  try {
                    // Update the news events in the backend
                    await newsEditorApi.updateNewsEvents(state.currentJob!.job_id, updatedEvents);

                    // Update the local state
                    const updatedJob = {
                      ...state.currentJob!,
                      newsAggregation: {
                        ...state.currentJob!.newsAggregation!,
                        news_events: updatedEvents
                      }
                    };
                    dispatch({ type: 'SET_CURRENT_JOB', payload: updatedJob });

                    console.log('News events updated successfully');
                  } catch (error) {
                    console.error('Error updating news events:', error);
                    alert('Failed to update news events');
                  }
                }}
                onRerun={(params) => handleStageRerun('aggregation', params)}
                queryTopic={state.currentJob.query_topic}
              />
            </PipelineStageEditor>

            {/* Script Generation Stage */}
            <PipelineStageEditor
              stageName="Script Generation"
              stageKey="script_generation"
              status={state.currentJob.stages.script_generation}
              isExpanded={state.expandedStages.includes('script_generation')}
              onToggleExpansion={() => dispatch({ type: 'TOGGLE_STAGE_EXPANSION', payload: 'script_generation' })}
              onRerun={() => handleStageRerun('script_generation')}
            >
              <ScriptGenerationEditor
                jobId={state.currentJob.job_id}
                script={state.currentJob.scriptGeneration || {
                  scenes: [],
                  metadata: {
                    total_duration: 0,
                    scene_count: 0,
                    last_updated: new Date().toISOString()
                  }
                }}
                onUpdate={async (updatedScript: any) => {
                  try {
                    // Update the script in the backend (placeholder for now)
                    console.log('Updating script:', updatedScript);

                    // Update the local state
                    const updatedJob = {
                      ...state.currentJob!,
                      scriptGeneration: updatedScript
                    };
                    dispatch({ type: 'SET_CURRENT_JOB', payload: updatedJob });

                    console.log('Script updated successfully');
                  } catch (error) {
                    console.error('Error updating script:', error);
                    alert('Failed to update script');
                  }
                }}
                onRerun={() => handleStageRerun('script_generation')}
              />
            </PipelineStageEditor>

            {/* Rendering Stage */}
            <PipelineStageEditor
              stageName="Rendering Engine"
              stageKey="rendering"
              status={state.currentJob.stages.rendering}
              isExpanded={state.expandedStages.includes('rendering')}
              onToggleExpansion={() => dispatch({ type: 'TOGGLE_STAGE_EXPANSION', payload: 'rendering' })}
              onRerun={() => handleStageRerun('rendering')}
            >
              <RenderingEditor
                jobId={state.currentJob.job_id}
                rendering={state.currentJob.renderingOutput || {
                  status: 'completed',
                  video_url: '/sample-video.mp4',
                  thumbnail_url: '/sample-thumbnail.jpg',
                  duration: 180,
                  last_updated: '2025-01-15T11:30:00Z'
                }}
                onUpdate={async (updatedRendering: any) => {
                  try {
                    // Update the rendering in the backend (placeholder for now)
                    console.log('Updating rendering:', updatedRendering);

                    // Update the local state
                    const updatedJob = {
                      ...state.currentJob!,
                      renderingOutput: updatedRendering
                    };
                    dispatch({ type: 'SET_CURRENT_JOB', payload: updatedJob });

                    console.log('Rendering updated successfully');
                  } catch (error) {
                    console.error('Error updating rendering:', error);
                    alert('Failed to update rendering');
                  }
                }}
                onRerun={handleRenderingRerun}
              />
            </PipelineStageEditor>

            {/* Composition Stage */}
            <PipelineStageEditor
              stageName="Composition"
              stageKey="composition"
              status={state.currentJob.stages.composition}
              isExpanded={state.expandedStages.includes('composition')}
              onToggleExpansion={() => dispatch({ type: 'TOGGLE_STAGE_EXPANSION', payload: 'composition' })}
              onRerun={() => {
                // For composition stage, we'll handle the rerun in the CompositionEditor component
                // This allows us to trigger both portrait and landscape composition generation
                console.log('Composition rerun triggered from pipeline stage');
              }}
            >
              <CompositionEditor
                jobId={state.currentJob.job_id}
                composition={state.currentJob.compositionOutput || {
                  status: 'completed',
                  final_video_url: '/final-video.mp4',
                  thumbnail_url: '/final-thumbnail.jpg',
                  duration: 180,
                  quality_score: 9.2,
                  last_updated: '2025-01-15T11:45:00Z',
                  composition_notes: 'Final composition completed with high quality output. All scenes properly synchronized and transitions smooth.'
                }}
                onUpdate={async (updatedComposition: any) => {
                  try {
                    // Update the composition in the backend (placeholder for now)
                    console.log('Updating composition:', updatedComposition);

                    // Update the local state
                    const updatedJob = {
                      ...state.currentJob!,
                      compositionOutput: updatedComposition
                    };
                    dispatch({ type: 'SET_CURRENT_JOB', payload: updatedJob });

                    console.log('Composition updated successfully');
                  } catch (error) {
                    console.error('Error updating composition:', error);
                    alert('Failed to update composition');
                  }
                }}
                onRerun={() => handleStageRerun('composition')}
              />
            </PipelineStageEditor>
          </section>
        )}
      </main>
    </div>
  );
};

export default NewsEditorPage;
